<template>
  <div class="relative min-h-screen overflow-hidden bg-slate-50">
    <!-- 背景流光动画：明亮、通透 -->
    <div class="pointer-events-none absolute inset-0 overflow-hidden">
      <div class="animate-blob absolute -left-20 top-0 h-96 w-96 rounded-full bg-indigo-200/40 blur-[100px] filter"></div>
      <div class="animate-blob animation-delay-2000 absolute -right-20 top-1/4 h-96 w-96 rounded-full bg-sky-200/40 blur-[100px] filter"></div>
      <div class="animate-blob animation-delay-4000 absolute bottom-1/4 left-1/3 h-96 w-96 rounded-full bg-purple-200/40 blur-[100px] filter"></div>
    </div>

    <div class="relative grid min-h-screen grid-cols-1 lg:grid-cols-[1.2fr_1fr] xl:grid-cols-[1.4fr_1fr]">
      <!-- 左侧：亮色优雅宣传区 -->
      <section class="relative hidden flex-col justify-center overflow-hidden px-12 lg:flex xl:px-24">
        <div class="relative z-10">
          <!-- 醒目的标题排版 -->
          <div class="mb-16">
            <h1 class="reveal-text text-5xl font-black tracking-tight text-slate-900 xl:text-6xl leading-tight">
              简单两步，<br />
              <span class="bg-gradient-to-r from-indigo-600 to-sky-500 bg-clip-text text-transparent">打造自己的教育品牌</span>
            </h1>
          </div>

          <!-- 两步极简流程：洗练、优雅的纵向流线设计 -->
          <div class="relative space-y-16">
            <!-- 纵向连接线 -->
            <div class="absolute left-7 top-10 h-[calc(100%-40px)] w-px bg-gradient-to-b from-indigo-500/50 via-sky-400/30 to-transparent"></div>

            <!-- Step 1 -->
            <div class="reveal-card group relative flex items-start gap-10">
              <div class="relative z-10 flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full border border-indigo-200 bg-white text-xl font-bold text-indigo-600 shadow-sm transition-all duration-500 group-hover:border-indigo-500 group-hover:bg-indigo-50 group-hover:shadow-indigo-100">
                1
              </div>
              <div class="pt-1">
                <h3 class="text-2xl font-bold tracking-tight text-slate-900">🎨 上传 Logo</h3>
                <p class="mt-3 max-w-md text-base leading-relaxed text-slate-500 font-light">
                  一键上传，应用于所有旗下产品
                </p>
              </div>
            </div>

            <!-- Step 2 -->
            <div class="reveal-card animation-delay-300 group relative flex items-start gap-10">
              <div class="relative z-10 flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full border border-indigo-200 bg-white text-xl font-bold text-indigo-600 shadow-sm transition-all duration-500 group-hover:border-indigo-500 group-hover:bg-indigo-50 group-hover:shadow-indigo-100">
                2
              </div>
              <div class="pt-1">
                <h3 class="text-2xl font-bold tracking-tight text-slate-900">🌐 绑定自己域名</h3>
                <p class="mt-3 max-w-md text-base leading-relaxed text-slate-500 font-light">
                  技术支持，可自由更换
                </p>
              </div>
            </div>
          </div>

          <div class="reveal-text animation-delay-1000 mt-20 flex items-center gap-4 text-xs font-bold uppercase tracking-widest text-slate-400">
            <div class="h-px w-12 bg-slate-200"></div>
            <span>Powered by Magly</span>
          </div>
        </div>
      </section>

      <!-- 右侧：恢复为您最原始的登录表单区（完全保持不动） -->
      <section class="flex flex-col items-center justify-center px-4 py-12 sm:px-6 lg:px-12 xl:px-16">
        <div class="w-full max-w-md">
          <div class="rounded-lg bg-white p-6 shadow-lg shadow-slate-200/70">
            <div class="mb-6 border-b border-gray-200 pb-4">
              <p class="text-xs font-semibold uppercase tracking-[0.22em] text-indigo-500">
                Tenant Console
              </p>
              <h1 class="mt-2 text-2xl font-semibold text-gray-900">租户控制台登录</h1>
              <p class="mt-2 text-sm text-gray-500">
                使用租户账号登录，管理品牌设置、团队成员与核心运营数据。
              </p>
            </div>

            <form class="space-y-4" @submit.prevent="handleLogin">
              <div>
                <label class="mb-2 block text-sm font-medium text-gray-700">登录名</label>
                <input
                  v-model.trim="loginName"
                  type="text"
                  placeholder="请输入登录名"
                  class="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>

              <div>
                <label class="mb-2 block text-sm font-medium text-gray-700">密码</label>
                <input
                  v-model="password"
                  type="password"
                  placeholder="请输入密码"
                  class="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                  minlength="8"
                />
              </div>

              <p
                v-if="errorMessage"
                class="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600"
              >
                {{ errorMessage }}
              </p>

              <button class="primary-btn w-full justify-center" type="submit" :disabled="isSubmitting">
                {{ isSubmitting ? "登录中..." : "进入控制台" }}
              </button>
            </form>

            <div class="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
              <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">演示体验</p>

              <div class="mt-4 grid gap-3 sm:grid-cols-2">
                <button
                  class="primary-btn inline-flex items-center justify-center rounded-xl px-4 py-3 text-sm"
                  type="button"
                  :disabled="isSubmitting"
                  @click="handleDemoExperience"
                >
                  {{ isSubmitting && isAutoDemoSubmitting ? "进入中..." : "一键进入演示控制台" }}
                </button>

                <a
                  v-if="preferredSiteUrl"
                  :href="preferredSiteUrl"
                  target="_blank"
                  rel="noreferrer"
                  class="inline-flex items-center justify-center rounded-xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-indigo-200 hover:text-indigo-600"
                >
                  访问演示网站
                </a>
              </div>
            </div>
          </div>
          
          <p class="mt-8 text-center text-sm text-gray-400">
            如果您还没有账号，请联系系统管理员进行分配。
          </p>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from "vue";
import { useRoute, useRouter } from "vue-router";

import { loginAPI } from "@/api/auth";
import { useToken } from "@/hooks/useToken";
import { useAuthStore } from "@/stores/auth";

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const { setToken } = useToken();

interface PublicTenantConfigResponse {
  tenant_config: {
    preferred_site_url: string;
  };
}

const DEMO_LOGIN_NAME = "demo";
const DEMO_PASSWORD = "demo1234";
const DEMO_WEBSITE_URL_FALLBACK = import.meta.env.VITE_TENANT_DEMO_WEBSITE_URL || "";

const loginName = ref("");
const password = ref("");
const isSubmitting = ref(false);
const isAutoDemoSubmitting = ref(false);
const errorMessage = ref("");
const preferredSiteUrl = ref("");

/**
 * 登录页固定使用浅色主题，避免继承已保存的暗黑模式。
 */
const applyLoginLightTheme = () => {
  document.documentElement.classList.remove("dark");
};

/**
 * 提交租户控制台登录。
 */
const handleLogin = async (credentials?: { loginName: string; password: string }) => {
  if (isSubmitting.value) {
    return;
  }

  errorMessage.value = "";
  isSubmitting.value = true;
  const resolvedLoginName = credentials?.loginName ?? loginName.value;
  const resolvedPassword = credentials?.password ?? password.value;

  try {
    const response = await loginAPI(resolvedLoginName, resolvedPassword);
    if (response.status !== 200 || !response.data) {
      errorMessage.value = response.message || "登录失败，请检查账号与密码";
      return;
    }

    setToken(response.data.token.accessToken);
    authStore.setAuthContext(response.data.account, response.data.tenant);
    const redirect = typeof route.query.redirect === "string" ? route.query.redirect : "/dashboard";
    router.push(redirect);
  } catch {
    errorMessage.value = "登录失败，请稍后重试";
  } finally {
    isSubmitting.value = false;
    isAutoDemoSubmitting.value = false;
  }
};

/**
 * 填入并尝试使用演示账号登录。
 */
const handleDemoExperience = async () => {
  loginName.value = DEMO_LOGIN_NAME;
  password.value = DEMO_PASSWORD;
  isAutoDemoSubmitting.value = true;
  await handleLogin({
    loginName: DEMO_LOGIN_NAME,
    password: DEMO_PASSWORD,
  });
};

/**
 * 读取当前 Host 对应的公开租户配置，用于展示真实绑定域名入口。
 */
const loadPublicTenantConfig = async () => {
  try {
    const response = await fetch("/tenant-console-api/tenant/config", {
      credentials: "include",
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      return;
    }

    const payload = (await response.json()) as {
      status?: number;
      data?: PublicTenantConfigResponse;
    };

    preferredSiteUrl.value = payload.data?.tenant_config?.preferred_site_url || DEMO_WEBSITE_URL_FALLBACK;
  } catch {
    preferredSiteUrl.value = DEMO_WEBSITE_URL_FALLBACK;
  }
};

onMounted(() => {
  applyLoginLightTheme();
  void loadPublicTenantConfig();

  if (route.query.demo === "1") {
    void handleDemoExperience();
  }
});

onBeforeUnmount(() => {
  // 登录后由主布局重新根据已保存偏好初始化主题。
});
</script>

<style scoped>
/* 背景光斑动画 */
.animate-blob {
  animation: blob 7s infinite;
}
.animation-delay-2000 {
  animation-delay: 2s;
}
.animation-delay-4000 {
  animation-delay: 4s;
}

@keyframes blob {
  0% { transform: translate(0px, 0px) scale(1); }
  33% { transform: translate(30px, -50px) scale(1.1); }
  66% { transform: translate(-20px, 20px) scale(0.9); }
  100% { transform: translate(0px, 0px) scale(1); }
}

/* 文字与卡片入场动画 */
@keyframes reveal {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}

.reveal-text {
  opacity: 0;
  animation: reveal 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.reveal-card {
  opacity: 0;
  animation: reveal 1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: 0.8s;
}

.animation-delay-300 { animation-delay: 0.3s; }
.animation-delay-500 { animation-delay: 0.5s; }
.animation-delay-700 { animation-delay: 0.7s; }
.animation-delay-1000 { animation-delay: 1s; }
</style>
